import Survicate from '@survicate/survicate-web-package/survicate_widget';
import {
  AnalyticsEvent,
  AnalyticsPlatform,
} from '../Context/AnalyticsProvider';
import { FeedbackProvider } from '../Context/FeedbackProvider';

declare global {
  interface Window {
    survicate?: {
      ready: boolean;
    };
  }
}

export interface SurvicateAnalyticsPlatformConfig {
  workspaceKey: string;
  feedbackSurveyId: string;
  storyFeedbackSurveyId: string;
  bookFeedbackSurveyId: string;
  bookVideoFeedbackSurveyId: string;
}

export var SURVICATE_INSTANCE: SurvicateAnalyticsPlatform;

export class SurvicateAnalyticsPlatform
  implements AnalyticsPlatform, FeedbackProvider
{
  constructor(public readonly config: SurvicateAnalyticsPlatformConfig) {
    // Unlike other providers, Survicate is sensitive to multiple init calls causing the page to freeze
    // This ensures that Survicate is initialized only once
    if (SURVICATE_INSTANCE) {
      return SURVICATE_INSTANCE;
    }
    SURVICATE_INSTANCE = this;

    Survicate.init({
      workspaceKey: config.workspaceKey,
    }).then(() => {
      // Unfortunately, setTimeout is necessary because Survicate doesn't start functioning correctly right after init.
      // Maybe it's related to React rendering lifecycle, i.e. html created by Survicate not on page yet
      // 1ms delay lets rendering finish and lets us show the button
      setTimeout(() => {
        // Shows the feedback button. Then, on mobile, we hide it with CSS and simulate a click on our menu item click
        this.initFeedbackSurvey(config.feedbackSurveyId);
        // Survicate removes the button once the survey is completed. We don't want this behavior.
        // When it's reopened after being previously completed, and then closed, Survicate removes the button again.
        Survicate.addEventListener(Survicate.ApiEvent.surveyCompleted, () => {
          this.initFeedbackSurvey(config.feedbackSurveyId);
        });
        Survicate.addEventListener(Survicate.ApiEvent.surveyClosed, () => {
          this.initFeedbackSurvey(config.feedbackSurveyId);
        });
      }, 1);
    });
  }

  getId() {
    return 'survicate';
  }

  isEventSupported(event: AnalyticsEvent) {
    // We'll support all events since Survicate can be configured to show surveys based on any event
    return true;
  }

  identifyUser(userId: string, metadata?: any) {
    Survicate.setVisitorTraits({
      user_id: userId, // Survicate + Mixpanel integration requires 'user_id' strictly
      ...metadata,
    });
  }

  resetUser() {
    // Survicate doesn't have a direct reset method, but we can clear by setting undefined
    this.identifyUser(undefined);
  }

  async trackEvent(event: AnalyticsEvent, metadata?: any): Promise<void> {
    // Use Survicate's invokeEvent method for event-based targeting
    // https://developers.survicate.com/javascript/methods/#event-based-targeting
    Survicate.invokeEvent(event, metadata);

    // Unfortunately, even though Survicate supports event-based targeting, we were unable to get it to work
    switch (event) {
      case AnalyticsEvent.BOOK_PUBLISHED:
        this.initFeedbackSurvey(this.config.bookFeedbackSurveyId);
        break;
      case AnalyticsEvent.BOOK_VIDEO_WATCH_COMPLETED:
        this.initFeedbackSurvey(this.config.bookVideoFeedbackSurveyId);
        break;
      default:
        break;
    }
  }

  isInitialized() {
    return !!document.querySelector('#survicate-box button.sv__survey');
  }

  showFeedbackModal() {
    // We need to show the button again, even if we did it on page load, because it gets hidden after you close/submit the feedback dialog
    const feedbackButton: HTMLButtonElement = document.querySelector(
      '#survicate-box button.sv__survey',
    );
    feedbackButton?.click();
  }

  private initFeedbackSurvey(surveyId: string) {
    Survicate.showSurvey(surveyId, {
      forceDisplay: true,
    });
  }
}

export function getSurvicateSurveyUrl(surveyId: string) {
  return `https://survey.survicate.com/${surveyId}`;
}
